import { apiClient, clearStoredSessionToken } from '@api/client';
import type {
  CRMAccount,
  CRMAccountPayload,
  CRMActivity,
  CRMCall,
  CRMCallCreatePayload,
  CRMCallDetail,
  CRMCallSession,
  CRMAIPanelResponse,
  CRMApolloConnectionTestRequest,
  CRMApolloConnectionTestResponse,
  CRMApolloEmailAccountOption,
  CRMApolloEnrollRequest,
  CRMApolloLeadImportCommitRequest,
  CRMApolloLeadImportCommitResponse,
  CRMApolloLeadImportPreviewRequest,
  CRMApolloLeadImportPreviewResponse,
  CRMApolloListOption,
  CRMApolloPeopleSearchFilterOptions,
  CRMApolloPeopleSearchRequest,
  CRMApolloPeopleSearchResponse,
  CRMApolloProspectEnrichmentRequest,
  CRMApolloProspectEnrichmentResponse,
  CRMApolloSequenceDetail,
  CRMApolloSequenceOption,
  CRMApolloTemplateOption,
  CRMApolloSequenceActionResponse,
  CRMApolloSyncResponse,
  CRMApolloUserOption,
  CRMApproveEmailRequest,
  CRMApproveEmailResponse,
  CRMDashboardSummary,
  CRMFollowUps,
  FounderInsightsOverview,
  CRMLead,
  CRMLeadConvertPayload,
  CRMLeadConvertResponse,
  CRMLeadImportCommitResponse,
  CRMLeadImportPreviewRequest,
  CRMLeadImportPreviewResponse,
  CRMLeadPayload,
  CRMLeadQualifyPayload,
  CRMNotePayload,
  CRMOpportunity,
  CRMSetting,
  CRMSettingUpdatePayload,
  CRMUser,
  CRMWorkQueueResponse,
  CurrentUser,
  LoginRequest,
  LoginResponse,
  PaginatedResponse,
  TenantSummary,
} from '@/types/api';

type ActivityFilters = {
  accountId?: string;
  leadId?: string;
  opportunityId?: string;
};

export async function login(request: LoginRequest) {
  const { data } = await apiClient.post<LoginResponse>('/auth/login', request);
  return data;
}

export async function logout() {
  await apiClient.post('/auth/logout');
  clearStoredSessionToken();
}

export async function switchActiveTenant(tenantId: string) {
  const { data } = await apiClient.post<TenantSummary>('/auth/active-tenant', {
    tenant_id: tenantId,
  });
  return data;
}

export async function fetchCurrentUser() {
  const { data } = await apiClient.get<CurrentUser>('/auth/me');
  return data;
}

export async function fetchDashboardSummary() {
  const { data } = await apiClient.get<CRMDashboardSummary>('/crm/dashboard/summary');
  return data;
}

export async function fetchUsers(): Promise<CRMUser[]> {
  const { data } = await apiClient.get<{ items: CRMUser[] }>('/crm/users');
  return data.items;
}

export async function fetchHotLeads(ownerUserId?: string) {
  const { data } = await apiClient.get<PaginatedResponse<CRMLead>>('/crm/dashboard/hot-leads', {
    params: ownerUserId ? { owner_user_id: ownerUserId } : undefined,
  });
  return data;
}

export async function fetchFollowUps(ownerUserId?: string) {
  const { data } = await apiClient.get<CRMFollowUps>('/crm/dashboard/follow-ups', {
    params: ownerUserId ? { owner_user_id: ownerUserId } : undefined,
  });
  return data;
}

export async function fetchAccounts(search?: string, status?: string) {
  const { data } = await apiClient.get<PaginatedResponse<CRMAccount>>('/crm/accounts', {
    params: { search, status, limit: 40 },
  });
  return data;
}

export async function fetchAccount(accountId: string) {
  const { data } = await apiClient.get<CRMAccount>(`/crm/accounts/${accountId}`);
  return data;
}

export async function createAccount(payload: CRMAccountPayload) {
  const { data } = await apiClient.post<CRMAccount>('/crm/accounts', payload);
  return data;
}

export async function updateAccount(accountId: string, payload: Partial<CRMAccountPayload>) {
  const { data } = await apiClient.patch<CRMAccount>(`/crm/accounts/${accountId}`, payload);
  return data;
}

export async function fetchLeads(search?: string, status?: string, limit = 40) {
  const { data } = await apiClient.get<PaginatedResponse<CRMLead>>('/crm/leads', {
    params: { search, status, limit },
  });
  return data;
}

export async function fetchLead(leadId: string) {
  const { data } = await apiClient.get<CRMLead>(`/crm/leads/${leadId}`);
  return data;
}

export async function createLead(payload: CRMLeadPayload) {
  const { data } = await apiClient.post<CRMLead>('/crm/leads', payload);
  return data;
}

export async function updateLead(leadId: string, payload: Partial<CRMLeadPayload>) {
  const { data } = await apiClient.patch<CRMLead>(`/crm/leads/${leadId}`, payload);
  return data;
}

export async function qualifyLead(leadId: string, payload: CRMLeadQualifyPayload) {
  const { data } = await apiClient.post<CRMLead>(`/crm/leads/${leadId}/qualify`, payload);
  return data;
}

export async function convertLead(leadId: string, payload: CRMLeadConvertPayload) {
  const { data } = await apiClient.post<CRMLeadConvertResponse>(`/crm/leads/${leadId}/convert`, payload);
  return data;
}

export async function previewLeadImport(payload: CRMLeadImportPreviewRequest) {
  const { data } = await apiClient.post<CRMLeadImportPreviewResponse>('/crm/imports/leads/preview', payload);
  return data;
}

export async function commitLeadImport(payload: CRMLeadImportPreviewRequest) {
  const { data } = await apiClient.post<CRMLeadImportCommitResponse>('/crm/imports/leads/commit', payload);
  return data;
}

export async function fetchActivities(filters: ActivityFilters) {
  const { data } = await apiClient.get<PaginatedResponse<CRMActivity>>('/crm/activities', {
    params: {
      account_id: filters.accountId,
      lead_id: filters.leadId,
      opportunity_id: filters.opportunityId,
      limit: 20,
    },
  });
  return data;
}

export async function fetchCallSession(roomName?: string) {
  const { data } = await apiClient.get<CRMCallSession>('/crm/calls/session', {
    params: roomName ? { room_name: roomName } : undefined,
  });
  return data;
}

export async function fetchCalls(filters: ActivityFilters & { contactId?: string; ownerUserId?: string; status?: string }) {
  const { data } = await apiClient.get<PaginatedResponse<CRMCall>>('/crm/calls', {
    params: {
      account_id: filters.accountId,
      lead_id: filters.leadId,
      contact_id: filters.contactId,
      opportunity_id: filters.opportunityId,
      owner_user_id: filters.ownerUserId,
      status: filters.status,
      limit: 20,
    },
  });
  return data;
}

export async function fetchCallDetail(callId: string) {
  const { data } = await apiClient.get<CRMCallDetail>(`/crm/calls/${callId}`);
  return data;
}

export async function createCall(payload: CRMCallCreatePayload) {
  const { data } = await apiClient.post<CRMCall>('/crm/calls', payload);
  return data;
}

export async function endCall(
  callId: string,
  payload: {
    status: string;
    ended_at?: string | null;
    duration_seconds?: number | null;
    recording_status?: string | null;
    transcript_status?: string | null;
    processing_status?: string | null;
    failure_reason?: string | null;
  },
) {
  const { data } = await apiClient.post<CRMCall>(`/crm/calls/${callId}/end`, payload);
  return data;
}

export async function addAccountNote(accountId: string, payload: CRMNotePayload) {
  const { data } = await apiClient.post<CRMActivity>(`/crm/accounts/${accountId}/notes`, payload);
  return data;
}

export async function addLeadNote(leadId: string, payload: CRMNotePayload) {
  const { data } = await apiClient.post<CRMActivity>(`/crm/leads/${leadId}/notes`, payload);
  return data;
}

export async function fetchOpportunities(stage?: string) {
  const { data } = await apiClient.get<PaginatedResponse<CRMOpportunity>>('/crm/opportunities', {
    params: { stage, limit: 40 },
  });
  return data;
}

export async function fetchSettings() {
  const { data } = await apiClient.get<CRMSetting>('/crm/settings');
  return data;
}

export async function updateSettings(payload: CRMSettingUpdatePayload) {
  const { data } = await apiClient.put<CRMSetting>('/crm/settings', payload);
  return data;
}

export async function testApolloConnection(payload?: CRMApolloConnectionTestRequest) {
  const { data } = await apiClient.post<CRMApolloConnectionTestResponse>('/crm/integrations/apollo/test', payload ?? {});
  return data;
}

export async function fetchApolloSequences(search?: string) {
  const { data } = await apiClient.get<{ items: CRMApolloSequenceOption[] }>('/crm/integrations/apollo/sequences', {
    params: search ? { search } : undefined,
  });
  return data.items;
}

export async function fetchApolloSequenceDetail(sequenceId: string) {
  const { data } = await apiClient.get<CRMApolloSequenceDetail>(`/crm/integrations/apollo/sequences/${sequenceId}`);
  return data;
}

export async function fetchApolloTemplates(search?: string) {
  const { data } = await apiClient.get<{ items: CRMApolloTemplateOption[] }>('/crm/integrations/apollo/templates', {
    params: search ? { search } : undefined,
  });
  return data.items;
}

export async function searchApolloPeople(payload: CRMApolloPeopleSearchRequest) {
  const { data } = await apiClient.post<CRMApolloPeopleSearchResponse>('/crm/integrations/apollo/search/people', payload);
  return data;
}

export async function fetchApolloPeopleSearchFilterOptions(qKeywords?: string, sampleSize = 100) {
  const { data } = await apiClient.get<CRMApolloPeopleSearchFilterOptions>('/crm/integrations/apollo/search/people/options', {
    params: {
      q_keywords: qKeywords || undefined,
      sample_size: sampleSize,
    },
  });
  return data;
}

export async function enrichApolloPerson(payload: CRMApolloProspectEnrichmentRequest) {
  const { data } = await apiClient.post<CRMApolloProspectEnrichmentResponse>(
    '/crm/integrations/apollo/enrich/person',
    payload,
  );
  return data;
}

export async function previewApolloLeadImport(payload: CRMApolloLeadImportPreviewRequest) {
  const { data } = await apiClient.post<CRMApolloLeadImportPreviewResponse>(
    '/crm/integrations/apollo/import/leads/preview',
    payload,
  );
  return data;
}

export async function commitApolloLeadImport(payload: CRMApolloLeadImportCommitRequest) {
  const { data } = await apiClient.post<CRMApolloLeadImportCommitResponse>(
    '/crm/integrations/apollo/import/leads/commit',
    payload,
  );
  return data;
}

export async function fetchApolloEmailAccounts() {
  const { data } = await apiClient.get<{ items: CRMApolloEmailAccountOption[] }>('/crm/integrations/apollo/email-accounts');
  return data.items;
}

export async function fetchApolloUsers() {
  const { data } = await apiClient.get<{ items: CRMApolloUserOption[] }>('/crm/integrations/apollo/users');
  return data.items;
}

export async function fetchApolloLists() {
  const { data } = await apiClient.get<{ items: CRMApolloListOption[] }>('/crm/integrations/apollo/lists');
  return data.items;
}

export async function syncLeadToApollo(leadId: string) {
  const { data } = await apiClient.post<CRMApolloSyncResponse>(`/crm/leads/${leadId}/apollo/sync`);
  return data;
}

export async function enrollLeadInApolloSequence(leadId: string, payload: CRMApolloEnrollRequest) {
  const { data } = await apiClient.post<CRMApolloSequenceActionResponse>(
    `/crm/leads/${leadId}/apollo/enroll`,
    payload,
  );
  return data;
}

export async function stopLeadInApolloSequence(leadId: string) {
  const { data } = await apiClient.post<CRMApolloSequenceActionResponse>(`/crm/leads/${leadId}/apollo/stop`);
  return data;
}

export async function downloadCRMExport(entity: 'accounts' | 'leads', format: 'csv' | 'json') {
  const response = await apiClient.get<Blob>(`/crm/export/${entity}`, {
    params: { format },
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `crm-${entity}.${format}`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

export async function fetchLeadAIPanel(leadId: string): Promise<CRMAIPanelResponse> {
  const { data } = await apiClient.get<CRMAIPanelResponse>(`/crm/leads/${leadId}/ai-panel`);
  return data;
}

export async function reEnrichLead(leadId: string): Promise<{ status: string }> {
  const { data } = await apiClient.post<{ status: string }>(`/crm/leads/${leadId}/re-enrich`);
  return data;
}

export async function approveLeadEmail(
  leadId: string,
  payload: CRMApproveEmailRequest,
): Promise<CRMApproveEmailResponse> {
  const { data } = await apiClient.post<CRMApproveEmailResponse>(
    `/crm/leads/${leadId}/approve-email`,
    payload,
  );
  return data;
}

export async function fetchWorkQueue(): Promise<CRMWorkQueueResponse> {
  const { data } = await apiClient.get<CRMWorkQueueResponse>('/crm/dashboard/work-queue');
  return data;
}

export async function fetchFounderInsightsOverview() {
  const { data } = await apiClient.get<FounderInsightsOverview>('/founder/insights/overview');
  return data;
}
